import { History, X } from 'lucide-react';
import { cn } from '@/lib/utils';

// The agent's last searches, shown under the search box while it is empty and
// focused. Purely presentational: the dashboard owns the list (see
// lib/recent-searches.ts), the highlighted index and the keyboard handling,
// so ArrowUp/ArrowDown/Enter work from the input itself.

export function RecentSearchesMenu({
  items,
  activeIndex,
  onPick,
  onRemove,
  onClear,
}: {
  items: string[];
  /** Index highlighted by the keyboard, or -1. */
  activeIndex: number;
  onPick: (q: string) => void;
  onRemove: (q: string) => void;
  onClear: () => void;
}) {
  if (items.length === 0) return null;
  return (
    <div
      role="listbox"
      aria-label="Recent searches"
      className="absolute left-0 right-0 top-full z-30 mt-1 overflow-hidden rounded-md border bg-popover p-1 text-popover-foreground shadow-md"
    >
      <div className="flex items-center justify-between px-2 pb-1 pt-0.5">
        <span className="text-3xs font-semibold uppercase tracking-wide text-muted-foreground">
          Recent searches
        </span>
        <button
          type="button"
          // mousedown, not click: the input blurs on mousedown and would
          // close the menu before a click lands.
          onMouseDown={(e) => {
            e.preventDefault();
            onClear();
          }}
          className="cursor-pointer rounded px-1 text-3xs text-muted-foreground hover:text-foreground hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
        >
          Clear history
        </button>
      </div>
      <ul>
        {items.map((q, i) => (
          <li key={q} role="option" aria-selected={i === activeIndex}>
            <div
              className={cn(
                'group/recent flex items-center gap-2 rounded px-2 py-1.5 text-xs',
                i === activeIndex ? 'bg-accent text-foreground' : 'hover:bg-accent/60',
              )}
            >
              <button
                type="button"
                onMouseDown={(e) => {
                  e.preventDefault();
                  onPick(q);
                }}
                className="flex min-w-0 flex-1 cursor-pointer items-center gap-2 text-left focus-visible:outline-none"
              >
                <History className="h-3 w-3 shrink-0 text-muted-foreground" aria-hidden />
                <span className="truncate">{q}</span>
              </button>
              <button
                type="button"
                onMouseDown={(e) => {
                  e.preventDefault();
                  onRemove(q);
                }}
                aria-label={`Remove "${q}" from recent searches`}
                className="shrink-0 cursor-pointer rounded p-0.5 text-muted-foreground opacity-0 hover:bg-background hover:text-foreground focus-visible:opacity-100 group-hover/recent:opacity-100"
              >
                <X className="h-3 w-3" />
              </button>
            </div>
          </li>
        ))}
      </ul>
    </div>
  );
}
